import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { animate, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { PlanPromptService } from '../../services/plan-prompt.service';
import { TalkToSalesService } from '../../services/talk-to-sales.service';
import { CurrencyService, LandingCurrency, LandingCurrencyCode } from '../../services/currency.service';

declare global {
  interface Window {
    _iub: any;
  }
}

@Component({
  selector: 'app-public-layout',
  standalone: false,
  templateUrl: './public-layout.component.html',
  styleUrl: './public-layout.component.scss',
  animations: [
    trigger('backdrop', [
      transition(':enter', [style({ opacity: 0 }), animate('200ms ease-out', style({ opacity: 1 }))]),
      transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
    ]),
    trigger('modal', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(16px) scale(0.96)' }),
        animate('250ms cubic-bezier(0.2, 0.8, 0.2, 1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(16px) scale(0.96)' })),
      ]),
    ]),
  ],
})
export class PublicLayoutComponent implements OnInit, OnDestroy {
  isMobileMenuOpen = false;
  planPromptOpen = false;
  isCurrencyMenuOpen = false;

  @ViewChild('publicHeader') private publicHeader!: ElementRef;
  @ViewChild('currencyToggle') private currencyToggle?: ElementRef;

  private auth = inject(AuthService);
  private router = inject(Router);
  private planPromptService = inject(PlanPromptService);
  private talkToSalesService = inject(TalkToSalesService);
  private currencyService = inject(CurrencyService);
  private promptSub?: Subscription;
  private currencySub?: Subscription;

  currentCurrency: LandingCurrency = this.currencyService.currency;
  currencies = this.currencyService.currencies;

  ngOnInit(): void {
    this.promptSub = this.planPromptService.isOpen$.subscribe((open) => {
      this.planPromptOpen = open;
      document.body.style.overflow = open ? 'hidden' : '';
    });

    this.currencySub = this.currencyService.currency$.subscribe((currency) => {
      this.currentCurrency = currency;
    });
  }

  ngOnDestroy(): void {
    this.promptSub?.unsubscribe();
    this.currencySub?.unsubscribe();
    document.body.style.overflow = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isMobileMenuOpen && this.publicHeader && !this.publicHeader.nativeElement.contains(event.target as Node)) {
      this.closeMobileMenu();
    }
    if (
      this.isCurrencyMenuOpen &&
      this.currencyToggle &&
      !this.currencyToggle.nativeElement.contains(event.target as Node)
    ) {
      this.isCurrencyMenuOpen = false;
    }
  }

  toggleCurrencyMenu(): void {
    this.isCurrencyMenuOpen = !this.isCurrencyMenuOpen;
  }

  selectCurrency(code: LandingCurrencyCode): void {
    this.currencyService.select(code);
    this.isCurrencyMenuOpen = false;
    this.navigateAndScrollToSection('pricing');
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onSignUp(): void {
    this.closeMobileMenu();
    this.planPromptService.open();
  }

  closePlanPrompt(): void {
    this.planPromptService.close();
  }

  choosePlan(): void {
    this.planPromptService.close();
    setTimeout(() => {
      const el = document.getElementById('pricing');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        this.router.navigate(['/'], { fragment: 'pricing' });
      }
    }, 220);
  }

  // Secondary CTA on the plan prompt: route users who aren't ready to self-serve to Talk to Sales
  // instead of letting them skip plan selection and register plan-less. The sales modal lives in
  // home-pricing.component (on '/'). If we're already on home, trigger it directly via the shared
  // service; otherwise navigate home with ?cta=contact, which home-pricing opens on init (avoids a
  // Subject timing race when the modal's component isn't mounted yet).
  talkToSales(): void {
    this.planPromptService.close();
    const onHome = this.router.url.split('#')[0].split('?')[0] === '/';
    if (onHome) {
      this.talkToSalesService.requestOpen();
    } else {
      this.router.navigate(['/'], { queryParams: { cta: 'contact' } });
    }
  }

  scrollToSection(event: Event, sectionId: string): void {
    event.preventDefault();
    this.closeMobileMenu();
    this.navigateAndScrollToSection(sectionId);
  }

  private navigateAndScrollToSection(sectionId: string): void {
    const target = document.getElementById(sectionId);
    if (!target) {
      const currentPath = this.router.url.split('#')[0].split('?')[0];
      if (currentPath !== '/') {
        this.router.navigate(['/'], { fragment: sectionId }).then(() => {
          setTimeout(() => this.scrollToSectionById(sectionId), 0);
        });
      }
      return;
    }

    this.scrollToTarget(target, sectionId);
  }

  private scrollToSectionById(sectionId: string): void {
    const target = document.getElementById(sectionId);
    if (!target) return;
    this.scrollToTarget(target, sectionId);
  }

  private scrollToTarget(target: HTMLElement, sectionId: string): void {
    const headerOffset = 72;
    const startPosition = window.scrollY;
    const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerOffset;
    const distance = targetPosition - startPosition;
    const duration = 900;
    const startTime = performance.now();

    const easeInOutCubic = (progress: number): number =>
      progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;

    const animateScroll = (currentTime: number): void => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, startPosition + distance * easeInOutCubic(progress));
      if (progress < 1) window.requestAnimationFrame(animateScroll);
      else history.replaceState(null, '', `#${sectionId}`);
    };

    window.scrollTo(0, startPosition + distance * easeInOutCubic(0.02));
    window.requestAnimationFrame(animateScroll);
  }

  onLogoClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.closeMobileMenu();

    this.auth.isAuthenticated$.pipe(take(1)).subscribe((isAuthenticated) => {
      if (!isAuthenticated) {
        if (this.router.url === '/') {
          this.scrollToTop();
        } else {
          this.router.navigate(['/']);
        }
        return;
      }
      this.auth.getDefaultDashboardUrl().pipe(take(1)).subscribe((url) => {
        this.router.navigateByUrl(url);
      });
    });
  }

  private scrollToTop(): void {
    const startPosition = window.scrollY;
    if (startPosition === 0) return;
    const duration = 700;
    const startTime = performance.now();
    const easeInOutCubic = (p: number): number =>
      p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
    const animate = (currentTime: number): void => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      window.scrollTo(0, startPosition * (1 - easeInOutCubic(progress)));
      if (progress < 1) window.requestAnimationFrame(animate);
    };
    window.requestAnimationFrame(animate);
  }

  login(): void {
    this.closeMobileMenu();
    this.auth.login();
  }
}
